<div class="page-container">
  <!-- 1. Page Header -->
  <div class="page-header">
    <div class="page-title-wrap">
      <h1>Refund Review Queue</h1>
      <p class="page-subtitle">
        Review customer refund requests, verify booth sessions, and issue replacement coupon vouchers.
      </p>
    </div>
    <div class="header-actions">
      <!-- Auto-Validation Feature Toggle (KAN-13) -->
      <div class="setting-toggle-box" [class.enabled]="autoValidationEnabled">
        <span class="setting-toggle-label">
          🤖 Auto-Validation: <strong>{{ autoValidationEnabled ? 'ON' : 'OFF' }}</strong>
        </span>
        <button 
          type="button" 
          class="toggle-switch-btn" 
          [class.active]="autoValidationEnabled" 
          [disabled]="isUpdatingSetting" 
          (click)="toggleAutoValidation()"
          title="Toggle automated AI validation and auto-coupon issuance">
          <span class="toggle-slider"></span>
        </button>
      </div>

      <button type="button" class="action-btn secondary-btn settings-btn" (click)="openSettingsModal()" title="Configure refund automation, deep log investigation, and semantic questions">
        ⚙️ Settings
      </button>

      <button type="button" class="action-btn primary-btn add-btn" (click)="openManualCreateModal()">
        ➕ Add Manual Refund
      </button>
    </div>
  </div>

  <!-- Page Alert Banners -->
  <div *ngIf="actionSuccessMessage && !isDetailModalOpen && !isManualCreateModalOpen" class="page-alert-banner success">
    ✓ {{ actionSuccessMessage }}
  </div>
  <div *ngIf="actionErrorMessage && !isDetailModalOpen && !isManualCreateModalOpen" class="page-alert-banner error">
    ⚠ {{ actionErrorMessage }}
  </div>

  <!-- 2. KPI / Dashboard Stat Cards -->
  <div class="dashboard-container">
    <div class="stat-card" [class.highlight-warning]="needsReviewCount > 0">
      <div class="stat-title">Needs Review</div>
      <div class="stat-value warning">{{ needsReviewCount }}</div>
      <div class="stat-sub">Pending staff decision</div>
    </div>
    <div class="stat-card">
      <div class="stat-title">Approved</div>
      <div class="stat-value success">{{ approvedCount }}</div>
      <div class="stat-sub">Coupons issued</div>
    </div>
    <div class="stat-card">
      <div class="stat-title">Denied</div>
      <div class="stat-value danger">{{ deniedCount }}</div>
      <div class="stat-sub">Rejected requests</div>
    </div>
    <div class="stat-card">
      <div class="stat-title">Total Refund Volume</div>
      <div class="stat-value">{{ totalRequestedAmount | currency }}</div>
      <div class="stat-sub">Gross original payments</div>
    </div>
  </div>

  <!-- 3. Filter Toolbar -->
  <div class="toolbar-container">
    <!-- Status Filter Buttons -->
    <div class="status-tab-group">
      <button 
        type="button" 
        class="tab-btn" 
        [class.active]="selectedStatusFilter === 'needs_review'"
        (click)="setStatusFilter('needs_review')">
        Needs Review ({{ needsReviewCount }})
      </button>
      <button 
        type="button" 
        class="tab-btn" 
        [class.active]="selectedStatusFilter === 'approved'"
        (click)="setStatusFilter('approved')">
        Approved ({{ approvedCount }})
      </button>
      <button 
        type="button" 
        class="tab-btn" 
        [class.active]="selectedStatusFilter === 'denied'"
        (click)="setStatusFilter('denied')">
        Denied ({{ deniedCount }})
      </button>
      <button 
        type="button" 
        class="tab-btn" 
        [class.active]="selectedStatusFilter === 'needs_more_info'"
        (click)="setStatusFilter('needs_more_info')">
        Needs More Info
      </button>
      <button 
        type="button" 
        class="tab-btn" 
        [class.active]="selectedStatusFilter === 'all'"
        (click)="setStatusFilter('all')">
        All Requests ({{ allRequests.length }})
      </button>
    </div>

    <!-- Search & Date Filters -->
    <div class="toolbar-controls">
      <div class="search-input-wrap">
        <input 
          type="text" 
          class="toolbar-search-input" 
          placeholder="Search by ticket ID (RF-...), email, machine, session..." 
          [(ngModel)]="searchQuery" 
          (input)="onSearchChange()" />
      </div>

      <div class="date-filter-wrap">
        <input 
          type="date" 
          class="toolbar-date-input" 
          [(ngModel)]="selectedDateFilter" 
          (change)="onDateFilterChange()" />
      </div>
    </div>
  </div>

  <!-- 4. Data Grid & Review List Section -->
  <div class="data-grid-container">
    <div class="table-scroll-hint">
      <span class="hint-icon">↔</span> Scroll table horizontally to view complete details and review actions
    </div>
    <div class="data-grid-scrollable">
      <table class="data-grid desktop-grid-table">
        <colgroup>
          <col class="col-ticket-date" />
          <col class="col-customer" />
          <col class="col-location" />
          <col class="col-incident" />
          <col class="col-financials" />
          <col class="col-risk-ai" />
          <col class="col-status" />
          <col class="col-actions" />
        </colgroup>
        <thead>
          <tr>
            <th class="col-ticket-date">Ticket / Date</th>
            <th class="col-customer">Customer Email</th>
            <th class="col-location">Machine / Location</th>
            <th class="col-incident">Incident / Session</th>
            <th class="col-financials text-right">Amount / Method</th>
            <th class="col-risk-ai text-center">Risk / AI</th>
            <th class="col-status text-center">Status</th>
            <th class="col-actions text-center sticky-action-col">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let request of paginatedRequests" class="data-row">
            <!-- Ticket + Date Group -->
            <td class="cell-ticket-date">
              <div class="two-line-group">
                <span class="ticket-id-pill font-mono">{{ request.ticketId || request.id }}</span>
                <span class="sub-date-text">{{ formatDate(request.createdAt) }}</span>
              </div>
            </td>

            <!-- Customer Email -->
            <td class="cell-customer">
              <span class="user-email-text" [title]="request.requestorEmail">{{ request.requestorEmail }}</span>
            </td>

            <!-- Machine / Location -->
            <td class="cell-location">
              <div class="two-line-group">
                <strong class="machine-name-text" [title]="request.deviceId || 'Unknown'">{{ request.deviceId || 'Unknown' }}</strong>
                <span class="store-loc-text" *ngIf="request.storeLocation" [title]="request.storeLocation">{{ request.storeLocation }}</span>
              </div>
            </td>

            <!-- Incident / Reason / Session ID -->
            <td class="cell-incident">
              <div class="two-line-group">
                <span class="reason-text" [title]="request.reasonCategory">{{ request.reasonCategory }}</span>
                <span class="session-code-pill" *ngIf="request.sessionId" [title]="request.sessionId">ID: {{ request.sessionId }}</span>
                <span class="no-session-text" *ngIf="!request.sessionId">Manual Search</span>
              </div>
            </td>

            <!-- Amount + Payment Method -->
            <td class="text-right cell-financials">
              <div class="financials-group">
                <div class="amount-value">{{ request.originalPaidAmount | currency }}</div>
                <div class="financials-badges">
                  <span class="badge-method" [class.method-qr]="request.identificationMethod === 'qr_scan'">
                    {{ request.identificationMethod === 'qr_scan' ? '📷 QR' : '✏️ Manual' }}
                  </span>
                  <span class="badge-cash" *ngIf="request.wantsCashRefund">💵 Cash</span>
                </div>
              </div>
            </td>

            <!-- Risk + AI Classification -->
            <td class="text-center cell-risk-ai">
              <div class="risk-badge-group">
                <span class="badge-risk" [ngClass]="getRiskBandClass(request)">
                  {{ getRiskLabel(request) }}
                </span>
                <span class="ai-class-chip" *ngIf="request.aiClassification" [title]="request.aiSummary || ''">
                  🤖 {{ request.aiClassification }}
                </span>
              </div>
            </td>

            <!-- Status -->
            <td class="text-center cell-status">
              <span class="status status-partial" *ngIf="request.status === 'needs_review'">Needs Review</span>
              <span class="status status-paid" *ngIf="request.status === 'approved'">Approved</span>
              <span class="status status-not-paid" *ngIf="request.status === 'denied'">Denied</span>
              <span class="status status-sent" *ngIf="request.status === 'needs_more_info'">More Info</span>
              <span class="status status-dim" *ngIf="request.status === 'collecting'">Collecting</span>
            </td>

            <!-- Sticky Action Column -->
            <td class="text-center cell-actions sticky-action-col">
              <button type="button" class="action-btn view-btn" (click)="openDetailModal(request)">
                👁️ Review
              </button>
            </td>
          </tr>

          <!-- Empty State -->
          <tr *ngIf="filteredRequests.length === 0">
            <td colspan="8" class="empty-cell">
              <div class="empty-state-box">
                <div class="empty-icon">📂</div>
                <div class="empty-title">No Refund Requests Found</div>
                <div class="empty-desc">There are no refund requests matching your current filter criteria.</div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Responsive Cards for Tablet / Mobile (<=1024px) -->
    <div class="responsive-card-list">
      <div *ngFor="let request of paginatedRequests" class="responsive-review-card">
        <div class="card-header-row">
          <div class="card-ticket-meta">
            <span class="ticket-id-pill font-mono">{{ request.ticketId || request.id }}</span>
            <span class="sub-date-text">{{ formatDate(request.createdAt) }}</span>
          </div>
          <div class="card-status-pill">
            <span class="status status-partial" *ngIf="request.status === 'needs_review'">Needs Review</span>
            <span class="status status-paid" *ngIf="request.status === 'approved'">Approved</span>
            <span class="status status-not-paid" *ngIf="request.status === 'denied'">Denied</span>
            <span class="status status-sent" *ngIf="request.status === 'needs_more_info'">More Info</span>
            <span class="status status-dim" *ngIf="request.status === 'collecting'">Collecting</span>
          </div>
        </div>

        <div class="card-body-grid">
          <div class="card-field">
            <span class="field-label">Customer</span>
            <span class="field-value user-email-text" [title]="request.requestorEmail">{{ request.requestorEmail }}</span>
          </div>
          <div class="card-field">
            <span class="field-label">Location / Device</span>
            <span class="field-value font-medium">{{ request.deviceId || 'Unknown' }} <span *ngIf="request.storeLocation" class="text-muted">({{ request.storeLocation }})</span></span>
          </div>
          <div class="card-field">
            <span class="field-label">Reason</span>
            <span class="field-value"><span class="reason-text">{{ request.reasonCategory }}</span></span>
          </div>
          <div class="card-field">
            <span class="field-label">Amount & Method</span>
            <span class="field-value font-bold amount-text">
              {{ request.originalPaidAmount | currency }}
              <span class="badge-method" [class.method-qr]="request.identificationMethod === 'qr_scan'">
                {{ request.identificationMethod === 'qr_scan' ? '📷 QR' : '✏️ Manual' }}
              </span>
              <span class="badge-cash" *ngIf="request.wantsCashRefund">💵 Cash</span>
            </span>
          </div>
          <div class="card-field card-field-full">
            <span class="field-label">Risk & AI Diagnosis</span>
            <div class="risk-badge-group-horizontal">
              <span class="badge-risk" [ngClass]="getRiskBandClass(request)">
                {{ getRiskLabel(request) }}
              </span>
              <span class="ai-class-chip" *ngIf="request.aiClassification" [title]="request.aiSummary || ''">
                🤖 {{ request.aiClassification }}
              </span>
            </div>
          </div>
        </div>

        <div class="card-footer-action">
          <button type="button" class="action-btn primary-btn card-review-btn" (click)="openDetailModal(request)">
            👁️ Review Request Details
          </button>
        </div>
      </div>

      <!-- Empty State for Cards -->
      <div *ngIf="filteredRequests.length === 0" class="empty-card-state">
        <div class="empty-icon">📂</div>
        <div class="empty-title">No Refund Requests Found</div>
        <div class="empty-desc">There are no refund requests matching your current filter criteria.</div>
      </div>
    </div>

    <!-- Pagination Controls -->
    <div class="pagination-controls" *ngIf="filteredRequests.length > 0">
      <div class="page-size-selector">
        <span>Rows per page:</span>
        <select [(ngModel)]="pageSize" (change)="onPageSizeChange()" class="page-size-select">
          <option *ngFor="let size of pageSizeOptions" [value]="size">{{ size }}</option>
        </select>
      </div>
      <div class="page-navigation">
        <span class="pagination-range-text">{{ paginationRange }}</span>
        <button type="button" class="pagination-btn" [disabled]="currentPage === 1" (click)="changePage(currentPage - 1)" title="Previous page">
          ‹
        </button>
        <span class="current-page-badge">{{ currentPage }} / {{ totalPages }}</span>
        <button type="button" class="pagination-btn" [disabled]="currentPage === totalPages" (click)="changePage(currentPage + 1)" title="Next page">
          ›
        </button>
      </div>
    </div>
  </div>
</div>

<!-- ==================== DETAIL / REVIEW MODAL ==================== -->
<div *ngIf="isDetailModalOpen && selectedRequest" class="modal-overlay" (click)="closeDetailModal()">
  <div class="app-modal review-modal" (click)="$event.stopPropagation()">
    <!-- Sticky Modal Header -->
    <div class="modal-sticky-header">
      <div class="header-left">
        <h2>Refund Request Details</h2>
        <span class="request-id-badge">ID: {{ selectedRequest.id }}</span>
      </div>
      <button type="button" class="close-btn-static" (click)="closeDetailModal()">&times;</button>
    </div>

    <!-- Alert Messages inside Modal -->
    <div *ngIf="actionSuccessMessage" class="modal-alert-banner success">
      ✓ {{ actionSuccessMessage }}
    </div>
    <div *ngIf="actionErrorMessage" class="modal-alert-banner error">
      ⚠ {{ actionErrorMessage }}
    </div>

    <!-- Modal Content Body -->
    <div class="modal-body-scrollable">
      <!-- Top Status Summary Banner -->
      <div class="status-summary-banner" [class.banner-approved]="selectedRequest.status === 'approved'" [class.banner-denied]="selectedRequest.status === 'denied'">
        <div class="status-left">
          <span class="status-label">Current Status:</span>
          <span class="status status-partial" *ngIf="selectedRequest.status === 'needs_review'">Needs Review</span>
          <span class="status status-paid" *ngIf="selectedRequest.status === 'approved'">Approved</span>
          <span class="status status-not-paid" *ngIf="selectedRequest.status === 'denied'">Denied</span>
          <span class="status status-sent" *ngIf="selectedRequest.status === 'needs_more_info'">Needs More Info</span>
        </div>
        <div class="coupon-issued-box" *ngIf="selectedRequest.couponCode">
          <span class="coupon-lbl">Issued Coupon Code:</span>
          <span class="coupon-code-text">{{ selectedRequest.couponCode }}</span>
        </div>
      </div>

      <!-- Cash Refund Alert Callout -->
      <div *ngIf="selectedRequest.wantsCashRefund" class="cash-alert-callout">
        <span class="callout-icon">💵</span>
        <div class="callout-content">
          <div class="callout-title">Customer Requested Cash Refund</div>
          <div class="callout-desc">This customer opted for a cash refund instead of a replacement coupon. Extended review and manual banking approval are required.</div>
        </div>
      </div>

      <!-- 48-Hour Retention Window Callout (KAN-47) -->
      <div *ngIf="selectedRequest.riskFactors?.includes('photos_unavailable_after_48h')" class="retention-alert-callout">
        <span class="callout-icon">⏳</span>
        <div class="callout-content">
          <div class="callout-title">Photos Unavailable After 48h Retention Window</div>
          <div class="callout-desc">This session was captured more than 48 hours ago and unclaimed online photos expired per retention policy. Automated coupon issuance was blocked for staff review.</div>
        </div>
      </div>

      <!-- Preservation Failure Callout (KAN-47) -->
      <div *ngIf="selectedRequest.riskFactors?.includes('preservation_failure_after_claim')" class="retention-alert-callout">
        <span class="callout-icon">⚠️</span>
        <div class="callout-content">
          <div class="callout-title">Saved Gallery Media Unavailable</div>
          <div class="callout-desc">The customer claimed this session to My Gallery, but its online media is currently unavailable. Queued for technical review to verify storage status.</div>
        </div>
      </div>

      <!-- High Risk Warning Callout -->
      <div *ngIf="(selectedRequest.riskBand === 'high' || (selectedRequest.riskScore && selectedRequest.riskScore >= 70)) && selectedRequest.status === 'needs_review'" class="high-risk-alert-callout">
        <span class="callout-icon">⚠️</span>
        <div class="callout-content">
          <div class="callout-title">High Risk Score Detected (Score: {{ selectedRequest.riskScore }}/100)</div>
          <div class="callout-desc">Automated validation flagged this request with elevated risk factors. Staff inspection of kiosk telemetry and transaction history is recommended before taking action.</div>
        </div>
      </div>

      <!-- Evidence & Detail Grids -->
      <div class="detail-section-grid">
        <!-- Customer & Request Meta -->
        <div class="detail-card">
          <div class="detail-card-title">Customer Information</div>
          <div class="detail-row">
            <span class="detail-label">Ticket ID:</span>
            <span class="detail-val font-mono font-bold">{{ selectedRequest.ticketId || selectedRequest.id }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">Google Email:</span>
            <span class="detail-val">{{ selectedRequest.requestorEmail }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">User UID:</span>
            <span class="detail-val font-mono">{{ selectedRequest.requestorUid }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">Requested At:</span>
            <span class="detail-val">{{ formatDate(selectedRequest.createdAt) }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">Entry Method:</span>
            <span class="detail-val">{{ selectedRequest.identificationMethod === 'qr_scan' ? 'Photo-Strip QR Camera Scan' : 'Guided Manual Entry' }}</span>
          </div>
        </div>

        <!-- Session & Machine Context -->
        <div class="detail-card">
          <div class="detail-card-title">Session & Machine Details</div>
          <div class="detail-row">
            <span class="detail-label">Machine ID:</span>
            <span class="detail-val font-bold">{{ selectedRequest.deviceId || 'Not specified' }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">Store / Location:</span>
            <span class="detail-val">{{ selectedRequest.storeLocation || selectedRequest.deviceId || 'Unknown' }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">Session ID:</span>
            <button type="button" *ngIf="selectedRequest.sessionId" class="session-link-btn font-mono" title="View session details" (click)="openSessionPanel()">
              {{ selectedRequest.sessionId }}
            </button>
            <span class="detail-val font-mono" *ngIf="!selectedRequest.sessionId">N/A (Manual Search)</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">Session Date / Time:</span>
            <span class="detail-val" *ngIf="getSessionWhen(selectedRequest) as when" data-testid="refund-session-when">
              {{ when.text }}
              <span class="session-when-hint" *ngIf="when.source === 'verified'">Exact start time, verified from the machine's session record</span>
              <span class="session-when-hint" *ngIf="when.source === 'submitted'">As submitted by the customer; the exact time is not verified yet</span>
            </span>
          </div>
        </div>

        <!-- User Refund History Card (Customer Activity & Lifetime Claims) -->
        <div class="detail-card user-history-card">
          <div class="detail-card-title user-history-title">
            <span>👤 User Refund History</span>
            <span class="history-total-pill" [class.highlight-warning]="getUserRefundHistory(selectedRequest).totalRequests > 1">
              {{ getUserRefundHistory(selectedRequest).totalRequests }} Total {{ getUserRefundHistory(selectedRequest).totalRequests === 1 ? 'Claim' : 'Claims' }}
            </span>
          </div>
          <div class="detail-row">
            <span class="detail-label">Lifetime Requests:</span>
            <span class="detail-val font-bold font-mono">
              {{ getUserRefundHistory(selectedRequest).totalRequests }} {{ getUserRefundHistory(selectedRequest).totalRequests === 1 ? 'time' : 'times' }}
            </span>
          </div>
          <div class="detail-row">
            <span class="detail-label">Prior Decisions:</span>
            <span class="detail-val history-breakdown-tags">
              <span class="history-tag approved" title="Approved requests">
                ✓ {{ getUserRefundHistory(selectedRequest).approvedCount }} Approved
              </span>
              <span class="history-tag denied" *ngIf="getUserRefundHistory(selectedRequest).deniedCount > 0" title="Denied requests">
                ✕ {{ getUserRefundHistory(selectedRequest).deniedCount }} Denied
              </span>
              <span class="history-tag pending" *ngIf="getUserRefundHistory(selectedRequest).pendingCount > 0" title="Pending review">
                ⏳ {{ getUserRefundHistory(selectedRequest).pendingCount }} Pending
              </span>
            </span>
          </div>
          <div class="detail-row" *ngIf="getUserRefundHistory(selectedRequest).totalRefundedAmount > 0">
            <span class="detail-label">Total Amount Refunded:</span>
            <span class="detail-val font-bold text-green">
              {{ getUserRefundHistory(selectedRequest).totalRefundedAmount | currency }}
            </span>
          </div>
          <div class="detail-row velocity-indicator-row" *ngIf="getUserRefundHistory(selectedRequest).totalRequests >= 3">
            <span class="detail-label">Velocity Notice:</span>
            <span class="detail-val velocity-warning">
              ⚠️ High Request Frequency ({{ getUserRefundHistory(selectedRequest).totalRequests }} claims on file)
            </span>
          </div>

          <!-- Mini Prior Claims List (if prior tickets exist) -->
          <div class="prior-claims-section" *ngIf="getUserRefundHistory(selectedRequest).priorRequests.length > 0">
            <div class="prior-claims-header">Previous Claim Tickets:</div>
            <div class="prior-claims-list">
              <div class="prior-claim-item" *ngFor="let prior of getUserRefundHistory(selectedRequest).priorRequests | slice:0:3">
                <span class="prior-ticket-id font-mono">{{ prior.ticketId || prior.id }}</span>
                <span class="prior-date">{{ formatDate(prior.createdAt) }}</span>
                <span class="prior-status-badge" [ngClass]="'status-' + (prior.status === 'approved' ? 'paid' : (prior.status === 'denied' ? 'not-paid' : 'partial'))">
                  {{ prior.status }}
                </span>
                <span class="prior-amount">{{ prior.originalPaidAmount | currency }}</span>
              </div>
              <div class="prior-claims-more" *ngIf="getUserRefundHistory(selectedRequest).priorRequests.length > 3">
                +{{ getUserRefundHistory(selectedRequest).priorRequests.length - 3 }} more previous requests
              </div>
            </div>
          </div>
        </div>

        <!-- Financial & Payment Details -->
        <div class="detail-card">
          <div class="detail-card-title">Financial & Payment Context</div>
          <div class="detail-row">
            <span class="detail-label">Payment Method:</span>
            <span class="detail-val">{{ selectedRequest.originalPaymentMethod }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">Original Paid Amount:</span>
            <span class="detail-val font-bold text-blue">{{ selectedRequest.originalPaidAmount | currency }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">Eligible Coupon Value:</span>
            <span class="detail-val font-bold text-green">{{ selectedRequest.eligibleCouponAmount | currency }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">Resolution Preference:</span>
            <span class="detail-val font-bold" [class.text-amber]="selectedRequest.wantsCashRefund" [class.text-green]="!selectedRequest.wantsCashRefund">
              {{ selectedRequest.wantsCashRefund ? '💵 Cash Refund Requested (Extended Review)' : '🎫 Replacement Coupon' }}
            </span>
          </div>
        </div>

        <!-- AI Validation & Risk Assessment Card -->
        <div class="detail-card ai-assessment-card" *ngIf="selectedRequest.aiSummary || selectedRequest.riskScore !== undefined">
          <div class="detail-card-title ai-card-title">
            <span>🤖 AI Validation & Risk Analysis</span>
            <span class="badge-risk" [ngClass]="getRiskBandClass(selectedRequest)">
              Score: {{ selectedRequest.riskScore ?? 0 }} / 100 ({{ (selectedRequest.riskBand || 'low') | uppercase }})
            </span>
          </div>

          <div class="ai-summary-box" *ngIf="selectedRequest.aiSummary">
            <div class="ai-summary-label">Automated Findings Summary (AC-017 / AC-018):</div>
            <p class="ai-summary-content">{{ selectedRequest.aiSummary }}</p>
          </div>

          <div class="detail-row" *ngIf="selectedRequest.aiClassification">
            <span class="detail-label">AI Classification:</span>
            <span class="detail-val font-bold font-mono">{{ selectedRequest.aiClassification | uppercase }} (Confidence: {{ ((selectedRequest.aiConfidence || 0) * 100).toFixed(0) }}%)</span>
          </div>

          <div class="risk-breakdown-section">
            <div class="risk-breakdown-header">
              <span class="breakdown-title">📊 Risk Score Calculation Breakdown:</span>
              <span class="breakdown-total font-mono font-bold">Total: {{ selectedRequest.riskScore ?? 0 }} / 100</span>
            </div>
            <div class="risk-breakdown-table-wrap">
              <table class="risk-breakdown-table">
                <thead>
                  <tr>
                    <th>Evaluated Factor</th>
                    <th>Policy & Verification Context</th>
                    <th class="text-right">Risk Pts</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let item of getRiskScoreBreakdown(selectedRequest)">
                    <td class="factor-name font-bold">{{ item.factor }}</td>
                    <td class="factor-desc">{{ item.description }}</td>
                    <td class="text-right factor-pts font-mono font-bold">+{{ item.points }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div class="evidence-snapshot-box" *ngIf="selectedRequest.evidenceSnapshot">
            <div class="snapshot-title">Hardware Telemetry & Verification Evidence:</div>
            <div class="snapshot-grid">
              <div class="snap-item">
                <span class="snap-label">Session on Machine:</span>
                <span class="snap-val" [class.text-green]="selectedRequest.evidenceSnapshot.sessionFound" [class.text-red]="!selectedRequest.evidenceSnapshot.sessionFound">
                  {{ selectedRequest.evidenceSnapshot.sessionFound ? '✓ Verified' : '✕ Not Found' }}
                </span>
              </div>
              <div class="snap-item">
                <span class="snap-label">Payment Confirmed:</span>
                <span class="snap-val" [class.text-green]="selectedRequest.evidenceSnapshot.paymentVerified" [class.text-red]="!selectedRequest.evidenceSnapshot.paymentVerified">
                  {{ selectedRequest.evidenceSnapshot.paymentVerified ? ('✓ ' + selectedRequest.evidenceSnapshot.verifiedPaymentMethod + ' ($' + selectedRequest.evidenceSnapshot.verifiedAmount + ')') : '✕ Unverified' }}
                </span>
              </div>
              <div class="snap-item">
                <span class="snap-label">Session Age:</span>
                <span class="snap-val">{{ selectedRequest.evidenceSnapshot.windowDaysAgo }} days ago</span>
              </div>
              <div class="snap-item">
                <span class="snap-label">Hardware Incident Logs:</span>
                <span class="snap-val">{{ selectedRequest.evidenceSnapshot.hardwareLogsFound }} events</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Reviewer Audit Information & Email Delivery Pipeline -->
        <div class="detail-card" *ngIf="selectedRequest.reviewedBy || selectedRequest.decisionRationale || selectedRequest.couponCode">
          <div class="detail-card-title">Staff Audit & Compensation Dispatch</div>
          <div class="detail-row" *ngIf="selectedRequest.couponCode">
            <span class="detail-label">Issued Coupon:</span>
            <span class="detail-val font-mono font-bold text-green">{{ selectedRequest.couponCode }}</span>
          </div>
          <div class="detail-row" *ngIf="selectedRequest.reviewedBy">
            <span class="detail-label">Reviewed By:</span>
            <span class="detail-val">{{ selectedRequest.reviewedBy }}</span>
          </div>
          <div class="detail-row" *ngIf="selectedRequest.reviewedAt">
            <span class="detail-label">Reviewed At:</span>
            <span class="detail-val">{{ formatDate(selectedRequest.reviewedAt) }}</span>
          </div>
          <div class="detail-row" *ngIf="selectedRequest.decisionRationale">
            <span class="detail-label">Decision Note:</span>
            <span class="detail-val">{{ selectedRequest.decisionRationale }}</span>
          </div>
          <div class="detail-row" *ngIf="selectedRequest.adminInquiry">
            <span class="detail-label">Customer Inquiry:</span>
            <span class="detail-val">{{ selectedRequest.adminInquiry }}</span>
          </div>
          <div class="detail-row" *ngIf="selectedRequest.couponCode">
            <span class="detail-label">Email Receipt:</span>
            <span class="detail-val">
              <span class="status status-paid" *ngIf="selectedRequest.emailDeliveryStatus === 'success'">Delivered</span>
              <span class="status status-partial" *ngIf="selectedRequest.emailDeliveryStatus === 'queued' || selectedRequest.emailDeliveryStatus === 'pending'">Queued</span>
              <span class="status status-not-paid" *ngIf="selectedRequest.emailDeliveryStatus === 'failed'">Delivery Failed</span>
              <span class="status status-dim" *ngIf="!selectedRequest.emailDeliveryStatus">Dispatched</span>
            </span>
          </div>
          <div class="detail-row" *ngIf="selectedRequest.couponCode && selectedRequest.emailDeliveryError">
            <span class="detail-label text-red">Delivery Error:</span>
            <span class="detail-val text-red">{{ selectedRequest.emailDeliveryError }}</span>
          </div>
        </div>
      </div>

      <!-- Problem / Reason Statement -->
      <div class="reason-statement-box">
        <div class="statement-header">
          <span class="statement-category-badge">{{ selectedRequest.reasonCategory }}</span>
          <span class="statement-subtitle">Customer Incident Report</span>
        </div>
        <p class="statement-body">
          {{ selectedRequest.reasonText || 'No additional text description provided by customer.' }}
        </p>
      </div>

      <!-- Attached Evidence & Files Box (KAN-24) -->
      <div class="evidence-box" *ngIf="evidenceManifests.length > 0 || isLoadingEvidence || selectedRequest.hasEvidence">
        <div class="evidence-box-header">
          <span class="evidence-box-title">Attached Evidence & Files ({{ evidenceManifests.length }})</span>
          <span class="evidence-security-badge">🔒 Sanitized WebP (EXIF / GPS Stripped)</span>
        </div>

        <div *ngIf="isLoadingEvidence && evidenceManifests.length === 0" class="evidence-loading-hint">
          <div class="mini-spinner"></div>
          <span>Loading attached files...</span>
        </div>

        <div *ngIf="evidenceManifests.length > 0" class="evidence-grid">
          <div 
            *ngFor="let item of evidenceManifests" 
            class="evidence-card"
            (click)="openEvidenceLightbox(item)">
            <div class="evidence-thumb-wrap">
              <img 
                *ngIf="evidenceUrls[item.id]" 
                [src]="evidenceUrls[item.id]" 
                [alt]="item.originalFilename" 
                class="evidence-thumb-img" />
              <div *ngIf="!evidenceUrls[item.id]" class="evidence-thumb-placeholder">
                <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                  <circle cx="8.5" cy="8.5" r="1.5"></circle>
                  <polyline points="21 15 16 10 5 21"></polyline>
                </svg>
              </div>
              <div class="evidence-hover-overlay">
                <span class="hover-text">🔍 View Full Image</span>
              </div>
            </div>
            <div class="evidence-meta">
              <span class="evidence-name" [title]="item.originalFilename">{{ item.originalFilename }}</span>
              <div class="evidence-meta-row">
                <span class="evidence-size" *ngIf="item.byteCount">{{ formatFileSize(item.byteCount) }}</span>
                <span class="evidence-dims" *ngIf="item.dimensions">{{ item.dimensions.width }}&times;{{ item.dimensions.height }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Action Footer -->
    <div class="modal-footer-actions">

      <button type="button" class="action-btn utility-btn" (click)="closeDetailModal()">Close</button>

      <ng-container *ngIf="selectedRequest.status === 'needs_review' || selectedRequest.status === 'needs_more_info'">
        <button type="button" class="action-btn utility-btn" (click)="openRequestInfoDialog()">
          💬 Request More Info
        </button>
        <button type="button" class="action-btn danger-btn" (click)="openDenyDialog()">
          ✕ Deny Request
        </button>
        <button type="button" class="action-btn primary-btn" (click)="openApproveDialog()">
          ✓ Approve & Issue Coupon
        </button>
      </ng-container>

      <ng-container *ngIf="selectedRequest.status === 'approved' && selectedRequest.couponCode">
        <button type="button" class="action-btn utility-btn" [disabled]="isProcessingAction" (click)="retryEmailDelivery()">
          ✉️ Resend Coupon Email
        </button>
      </ng-container>
    </div>

    <!-- Session Details Slide-Out Panel -->
    <aside class="session-slideout" [class.open]="isSessionPanelOpen" [attr.aria-hidden]="!isSessionPanelOpen">
      <div class="slideout-header">
        <h3>Session Details</h3>
        <button type="button" class="close-btn-static" aria-label="Close session details" (click)="closeSessionPanel()">&times;</button>
      </div>
      <div class="slideout-body">
        <div class="slideout-state" *ngIf="isSessionLoading">Loading session…</div>
        <div class="slideout-state slideout-error" *ngIf="!isSessionLoading && sessionPanelError">{{ sessionPanelError }}</div>

        <ng-container *ngIf="!isSessionLoading && sessionDetail">
          <div class="detail-row">
            <span class="detail-label">Session ID:</span>
            <span class="detail-val font-mono">{{ sessionDetail.sessionId }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">Time:</span>
            <span class="detail-val">{{ sessionDetail.Timestamp?.seconds ? formatPacificTime(sessionDetail.Timestamp) : 'N/A' }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">Payment:</span>
            <span class="detail-val">{{ sessionDetail.PaymentMethod }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">Amount Paid:</span>
            <span class="detail-val">{{ sessionDetail.AmountPaid | currency }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">Overpaid:</span>
            <span class="detail-val">{{ (sessionDetail.OverpaidAmount || 0) | currency }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">Print Cut:</span>
            <span class="detail-val">{{ sessionDetail.CutStyle || 'N/A' }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">Frame:</span>
            <span class="detail-val">{{ sessionDetail.FrameId || sessionDetail.ThemeFrameGroupId || 'N/A' }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">Theme:</span>
            <span class="detail-val">{{ sessionDetail.Theme || 'N/A' }}</span>
          </div>
          <div class="detail-row" *ngIf="sessionDetail.AnimefiedGenerated || sessionDetail.AnimefiedUsed">
            <span class="detail-label">Animefied:</span>
            <span class="detail-val">Gen: {{ sessionDetail.AnimefiedGenerated || 0 }} / Used: {{ sessionDetail.AnimefiedUsed || 0 }}</span>
          </div>
        </ng-container>

        <a *ngIf="selectedRequest.sessionId" class="action-btn primary-btn slideout-portal-btn" [href]="getSessionPortalUrl()" target="_blank" rel="noopener">
          Open in User Portal ↗
        </a>
      </div>
    </aside>
  </div>
</div>

<!-- ==================== SUB-MODAL: APPROVE & ISSUE COUPON ==================== -->
<div *ngIf="isApproveModalOpen" class="modal-overlay nested-modal" (click)="closeApproveDialog()">
  <div class="app-modal sub-action-modal" (click)="$event.stopPropagation()">
    <div class="modal-sticky-header">
      <h2>Approve Refund & Issue Coupon</h2>
      <button type="button" class="close-btn-static" (click)="closeApproveDialog()">&times;</button>
    </div>

    <div class="modal-body-scrollable">
      <p class="dialog-explanation">
        Approving this request will immediately generate a single-use coupon in the system and dispatch an email confirmation to <strong>{{ selectedRequest?.requestorEmail }}</strong>.
      </p>

      <div class="form-grid-dialog">
        <div class="form-group full-width">
          <label class="form-label">Coupon Promotion Title</label>
          <input type="text" class="form-input" [(ngModel)]="approveCouponTitle" />
        </div>
        <div class="form-group">
          <label class="form-label">Discount Type</label>
          <select class="form-select" [(ngModel)]="approveDiscountType">
            <option value="free_session">Free Session (100% OFF)</option>
            <option value="fixed">Fixed Dollar ($ OFF)</option>
            <option value="percent">Percentage (% OFF)</option>
          </select>
        </div>
        <div class="form-group">
          <label class="form-label">Discount Value ($ USD / %)</label>
          <input type="number" step="0.01" class="form-input" [(ngModel)]="approveDiscountValue" />
        </div>
        <div class="form-group full-width">
          <label class="form-label">Approval Notes / Rationale (Optional)</label>
          <textarea class="form-textarea" rows="2" placeholder="e.g. Approved due to verified printer paper jam..." [(ngModel)]="approveNotes"></textarea>
        </div>
      </div>
    </div>

    <div class="modal-footer-actions">
      <button type="button" class="action-btn utility-btn" (click)="closeApproveDialog()">Cancel</button>
      <button type="button" class="action-btn primary-btn" [disabled]="isProcessingAction" (click)="confirmApprove()">
        {{ isProcessingAction ? 'Issuing Coupon...' : 'Confirm & Issue Coupon' }}
      </button>
    </div>
  </div>
</div>

<!-- ==================== SUB-MODAL: DENY REQUEST ==================== -->
<div *ngIf="isDenyModalOpen" class="modal-overlay nested-modal" (click)="closeDenyDialog()">
  <div class="app-modal sub-action-modal" (click)="$event.stopPropagation()">
    <div class="modal-sticky-header">
      <h2>Deny Refund Request</h2>
      <button type="button" class="close-btn-static" (click)="closeDenyDialog()">&times;</button>
    </div>

    <div class="modal-body-scrollable">
      <p class="dialog-explanation">
        Please provide a clear rationale for denying this refund request for customer <strong>{{ selectedRequest?.requestorEmail }}</strong>.
      </p>

      <div class="form-group">
        <label class="form-label">Denial Rationale (Mandatory for Audit)</label>
        <textarea 
          class="form-textarea" 
          rows="3" 
          placeholder="e.g. Session verified completed with full print output; no transaction failure found..." 
          [(ngModel)]="denyRationale">
        </textarea>
      </div>
    </div>

    <div class="modal-footer-actions">
      <button type="button" class="action-btn utility-btn" (click)="closeDenyDialog()">Cancel</button>
      <button type="button" class="action-btn danger-btn" [disabled]="isProcessingAction || !denyRationale.trim()" (click)="confirmDeny()">
        {{ isProcessingAction ? 'Processing...' : 'Confirm Denial' }}
      </button>
    </div>
  </div>
</div>

<!-- ==================== SUB-MODAL: REQUEST MORE INFO ==================== -->
<div *ngIf="isRequestInfoModalOpen" class="modal-overlay nested-modal" (click)="closeRequestInfoDialog()">
  <div class="app-modal sub-action-modal" (click)="$event.stopPropagation()">
    <div class="modal-sticky-header">
      <h2>Request Additional Information</h2>
      <button type="button" class="close-btn-static" (click)="closeRequestInfoDialog()">&times;</button>
    </div>

    <div class="modal-body-scrollable">
      <p class="dialog-explanation">
        Specify what additional details or receipts are needed from <strong>{{ selectedRequest?.requestorEmail }}</strong> before an approval decision can be made.
      </p>

      <div class="form-group">
        <label class="form-label">Inquiry Message</label>
        <textarea 
          class="form-textarea" 
          rows="3" 
          placeholder="e.g. Please provide your bank statement transaction timestamp or booth photo..." 
          [(ngModel)]="requestInfoMessage">
        </textarea>
      </div>
    </div>

    <div class="modal-footer-actions">
      <button type="button" class="action-btn utility-btn" (click)="closeRequestInfoDialog()">Cancel</button>
      <button type="button" class="action-btn primary-btn" [disabled]="isProcessingAction || !requestInfoMessage.trim()" (click)="confirmRequestInfo()">
        {{ isProcessingAction ? 'Updating...' : 'Save Inquiry' }}
      </button>
    </div>
  </div>
</div>

<!-- ==================== MODAL: CREATE MANUAL REFUND ==================== -->
<div *ngIf="isManualCreateModalOpen" class="modal-overlay" (click)="closeManualCreateModal()">
  <div class="app-modal manual-create-modal" (click)="$event.stopPropagation()">
    <!-- Sticky Header -->
    <div class="modal-sticky-header">
      <div class="header-left">
        <h2>➕ Create Manual Refund</h2>
      </div>
      <button type="button" class="close-btn-static" (click)="closeManualCreateModal()">&times;</button>
    </div>

    <!-- Alert inside Modal -->
    <div *ngIf="actionErrorMessage" class="modal-alert-banner error">
      ⚠ {{ actionErrorMessage }}
    </div>

    <!-- Scrollable Body -->
    <div class="modal-body-scrollable">
      <p class="dialog-explanation">
        Manually record a customer compensation or refund request. You can choose to immediately issue a single-use coupon or route the request into the review queue.
      </p>

      <!-- Customer & Session Info Grid -->
      <div class="form-section-title">Customer & Session Information</div>
      <div class="form-grid-dialog">
        <div class="form-group full-width">
          <label class="form-label">Customer Google Email <span class="required-star">*</span></label>
          <input 
            type="email" 
            class="form-input" 
            placeholder="customer@gmail.com" 
            [(ngModel)]="manualEmail" 
            required />
        </div>

        <div class="form-group">
          <label class="form-label" for="manual-refund-brand">Brand <span class="required-star">*</span></label>
          <select 
            id="manual-refund-brand"
            class="form-select" 
            [(ngModel)]="manualBrandId" 
            (change)="onManualBrandChange()" 
            required>
            <option value="">-- Select Brand --</option>
            <option *ngFor="let b of allBrands" [value]="b.id">
              {{ b.name }}
            </option>
          </select>
        </div>

        <div class="form-group">
          <label class="form-label" for="manual-refund-device">Device ID</label>
          <select 
            id="manual-refund-device"
            class="form-select font-mono" 
            [(ngModel)]="manualDeviceId" 
            (change)="onManualDeviceChange()">
            <option value="">-- Select Device ID (Optional) --</option>
            <option *ngFor="let dev of getFilteredManualDevices()" [value]="dev.deviceId || dev.id">
              {{ dev.deviceId || dev.id }} {{ (dev.deviceName && dev.deviceName !== (dev.deviceId || dev.id)) ? '(' + dev.deviceName + ')' : (dev.name && dev.name !== (dev.deviceId || dev.id) ? '(' + dev.name + ')' : '') }}
            </option>
          </select>
        </div>

        <div class="form-group">
          <label class="form-label" for="manual-refund-store">Store Location</label>
          <select id="manual-refund-store" class="form-select" [(ngModel)]="manualStore">
            <option value="">-- Select Store (Optional) --</option>
            <option *ngFor="let store of (stores$ | async)" [value]="store.name || store.friendlyName || store.id">
              {{ store.name || store.friendlyName || store.id }}
            </option>
          </select>
        </div>

        <div class="form-group">
          <label class="form-label" for="manual-refund-session">Session ID (If Known)</label>
          <input 
            id="manual-refund-session"
            type="text" 
            class="form-input font-mono" 
            placeholder="e.g. 20260921-143022-SJC01" 
            [(ngModel)]="manualSessionId" />
        </div>

        <div class="form-group">
          <label class="form-label">Session Date (Pacific Time)</label>
          <input 
            type="date" 
            class="form-input" 
            [(ngModel)]="manualSessionDate" />
        </div>

        <div class="form-group">
          <label class="form-label">Payment Method</label>
          <select class="form-select" [(ngModel)]="manualPaymentMethod">
            <option value="Credit Card">Credit Card / Contactless</option>
            <option value="Cash">Cash (Bill Validator)</option>
            <option value="Apple Pay">Apple Pay</option>
            <option value="Google Pay">Google Pay</option>
            <option value="Other">Other</option>
          </select>
        </div>

        <div class="form-group">
          <label class="form-label">Original Paid Amount ($ USD) <span class="required-star">*</span></label>
          <input 
            type="number" 
            step="0.01" 
            min="0.01" 
            class="form-input" 
            [(ngModel)]="manualAmount" 
            required />
        </div>
      </div>

      <!-- Incident & Reason -->
      <div class="form-section-title">Incident Category & Details</div>
      <div class="form-grid-dialog">
        <div class="form-group full-width">
          <label class="form-label">Reason Category</label>
          <select class="form-select" [(ngModel)]="manualReasonCategory">
            <option value="Booth charged but did not start">Booth charged but did not start</option>
            <option value="Photos did not print">Photos did not print</option>
            <option value="Printer jammed / unusable output">Printer jammed / unusable output</option>
            <option value="Photo quality or lighting issue">Photo quality or lighting issue</option>
            <option value="Customer dispute / Staff courtesy">Customer dispute / Staff courtesy</option>
            <option value="Other / Staff Compensation">Other / Staff Compensation</option>
          </select>
        </div>

        <div class="form-group full-width">
          <label class="form-label">Incident Description / Internal Notes</label>
          <textarea 
            class="form-textarea" 
            rows="2" 
            placeholder="e.g. Customer reported paper ran out during print stage; staff verified with terminal log." 
            [(ngModel)]="manualReasonText">
          </textarea>
        </div>

        <div class="form-group full-width">
          <label class="manual-cash-checkbox-label">
            <input type="checkbox" [(ngModel)]="manualWantsCash" (change)="onManualCashToggle()" />
            <span class="manual-cash-checkbox-text">💵 Customer requested cash refund (flag as Cash Requested)</span>
          </label>
        </div>
      </div>

      <!-- Decision Mode Radio -->
      <div class="form-section-title">Resolution Action</div>
      <div class="decision-mode-container">
        <label class="radio-card" [class.selected]="manualDecisionMode === 'instant_approve'" [class.disabled]="manualWantsCash">
          <input 
            type="radio" 
            name="decisionMode" 
            value="instant_approve" 
            [disabled]="manualWantsCash"
            [(ngModel)]="manualDecisionMode" />
          <div class="radio-card-content">
            <div class="radio-card-title">⚡ Approve & Issue Replacement Coupon Immediately</div>
            <div class="radio-card-desc">
              {{ manualWantsCash ? 'Disabled: Cash refunds cannot be auto-issued and must be reviewed.' : 'Instantly generates a 12-character single-use coupon in the database and queues an email notification.' }}
            </div>
          </div>
        </label>

        <label class="radio-card" [class.selected]="manualDecisionMode === 'review_queue'">
          <input 
            type="radio" 
            name="decisionMode" 
            value="review_queue" 
            [(ngModel)]="manualDecisionMode" />
          <div class="radio-card-content">
            <div class="radio-card-title">📥 Add to Needs Review Queue</div>
            <div class="radio-card-desc">Creates a record with status 'Needs Review' so team members can inspect and approve later.</div>
          </div>
        </label>
      </div>

      <!-- Instant Coupon Config if Auto-Approve -->
      <div class="coupon-config-subcard" *ngIf="manualDecisionMode === 'instant_approve'">
        <div class="form-grid-dialog">
          <div class="form-group full-width">
            <label class="form-label">Coupon Promotion Title</label>
            <input type="text" class="form-input" [(ngModel)]="manualCouponTitle" />
          </div>

          <div class="form-group">
            <label class="form-label">Discount Type</label>
            <select class="form-select" [(ngModel)]="manualDiscountType">
              <option value="free_session">Free Session (100% OFF)</option>
              <option value="fixed">Fixed Dollar ($ OFF)</option>
              <option value="percent">Percentage (% OFF)</option>
            </select>
          </div>

          <div class="form-group">
            <label class="form-label">Discount Value ($ USD / %)</label>
            <input type="number" step="0.01" class="form-input" [(ngModel)]="manualDiscountValue" />
          </div>

          <div class="form-group full-width">
            <label class="form-label">Staff Approval Note (Optional)</label>
            <input type="text" class="form-input" placeholder="e.g. Manually authorized by store supervisor..." [(ngModel)]="manualNotes" />
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Footer Actions -->
    <div class="modal-footer-actions">
      <button type="button" class="action-btn utility-btn" (click)="closeManualCreateModal()">Cancel</button>
      <button 
        type="button" 
        class="action-btn primary-btn" 
        [disabled]="isProcessingAction || !manualEmail.trim() || !manualAmount" 
        (click)="confirmManualCreate()">
        {{ isProcessingAction ? 'Creating...' : (manualDecisionMode === 'instant_approve' ? '✓ Create & Issue Coupon' : '📥 Save to Review Queue') }}
      </button>
    </div>
  </div>
</div>

<!-- ========================================== -->
<!-- 6. REFUND & INVESTIGATION SETTINGS MODAL   -->
<!-- ========================================== -->
<div class="modal-overlay" *ngIf="isSettingsModalOpen" (click)="closeSettingsModal()">
  <div class="app-modal settings-modal" (click)="$event.stopPropagation()">
    <!-- Modal Sticky Header -->
    <div class="modal-sticky-header">
      <div class="modal-header-titles">
        <h2>⚙️ Refund Automation & Forensic Settings</h2>
        <span class="modal-subtitle">Configure AI auto-validation, deep machine log forensics, and custom inquiry questions.</span>
      </div>
      <button type="button" class="modal-close-btn" (click)="closeSettingsModal()">&times;</button>
    </div>

    <!-- Modal Scrollable Body -->
    <div class="modal-body-scrollable">
      <!-- Section 1: Core Automated Validation -->
      <div class="settings-section-card">
        <div class="settings-section-header">
          <div class="settings-icon">🤖</div>
          <div class="settings-text-block">
            <h3>Automated Validation & Auto-Coupon Issuance</h3>
            <p>Controls whether incoming refund claims are evaluated automatically by the AI policy engine.</p>
          </div>
          <div class="settings-control-toggle">
            <button 
              type="button" 
              class="toggle-switch-btn" 
              [class.active]="autoValidationEnabled" 
              (click)="autoValidationEnabled = !autoValidationEnabled">
              <span class="toggle-slider"></span>
            </button>
          </div>
        </div>
        <div class="settings-status-note" [class.enabled]="autoValidationEnabled">
          Status: <strong>{{ autoValidationEnabled ? 'ENABLED (Requests evaluated upon receipt)' : 'DISABLED (All requests route directly to Needs Review)' }}</strong>
        </div>
      </div>

      <!-- Section 2: Deep Semantic Log Investigation -->
      <div class="settings-section-card">
        <div class="settings-section-header">
          <div class="settings-icon">🔍</div>
          <div class="settings-text-block">
            <h3>Deep Machine Log Forensic Investigation</h3>
            <p>When enabled, the validation engine inspects actual machine Serilog (<code>applog</code>) and payment terminal (<code>POSLog</code>) files to confirm camera disconnects, mid-session freezes, printer jams, and transaction drops.</p>
          </div>
          <div class="settings-control-toggle">
            <button 
              type="button" 
              class="toggle-switch-btn" 
              [class.active]="deepLogInvestigationEnabled" 
              (click)="deepLogInvestigationEnabled = !deepLogInvestigationEnabled">
              <span class="toggle-slider"></span>
            </button>
          </div>
        </div>

        <div class="settings-suboptions" *ngIf="deepLogInvestigationEnabled">
          <div class="suboption-row">
            <div class="suboption-info">
              <span class="suboption-title">Auto-Signal Online Kiosks</span>
              <span class="suboption-desc">Automatically send <code>UPLOAD_LOGS</code> to live photobooths if the target date's log is not yet archived in Cloud Storage.</span>
            </div>
            <input 
              type="checkbox" 
              class="checkbox-input" 
              [(ngModel)]="deepLogAutoFetchKiosk" />
          </div>

          <div class="suboption-row">
            <div class="suboption-info">
              <span class="suboption-title">Kiosk Log Polling Timeout</span>
              <span class="suboption-desc">Maximum seconds to wait for a live kiosk to upload its log before falling back to review queue.</span>
            </div>
            <div class="suboption-input-wrap">
              <input 
                type="number" 
                min="5" 
                max="60" 
                class="form-input number-input" 
                [(ngModel)]="investigationTimeoutSec" />
              <span class="input-unit">sec</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Section 3: Semantic Inquiry Questions Catalog -->
      <div class="settings-section-card">
        <div class="settings-section-header">
          <div class="settings-icon">💬</div>
          <div class="settings-text-block">
            <h3>Custom Semantic Forensic Inquiries</h3>
            <p>Targeted natural language questions evaluated by Google Gemini against raw kiosk log text. Built-in checks include Camera Disconnects, Premature Mid-Session Stops, and Print Confirmations.</p>
          </div>
        </div>

        <!-- Add New Semantic Question Input -->
        <div class="add-question-bar">
          <input 
            type="text" 
            class="form-input" 
            placeholder="e.g. Did the camera disconnect during photo taking? or Was the transaction reversed in POSLog?" 
            [(ngModel)]="newSemanticQuestion" 
            (keyup.enter)="addCustomSemanticQuestion()" />
          <button 
            type="button" 
            class="action-btn primary-btn add-q-btn" 
            [disabled]="!newSemanticQuestion.trim()" 
            (click)="addCustomSemanticQuestion()">
            + Add Question
          </button>
        </div>

        <!-- Questions List -->
        <div class="questions-list" *ngIf="customSemanticQuestions.length > 0">
          <div class="question-chip-row" *ngFor="let q of customSemanticQuestions; let i = index">
            <span class="question-index">#{{ i + 1 }}</span>
            <span class="question-text">"{{ q }}"</span>
            <button type="button" class="remove-q-btn" (click)="removeCustomSemanticQuestion(i)" title="Remove question">&times;</button>
          </div>
        </div>

        <div class="empty-questions-hint" *ngIf="customSemanticQuestions.length === 0">
          No custom questions added yet. The system will use standard diagnostic inquiries and dynamic questions synthesized from customer notes.
        </div>
      </div>
    </div>

    <!-- Modal Footer Actions -->
    <div class="modal-footer-actions">
      <button type="button" class="action-btn utility-btn" (click)="closeSettingsModal()">Cancel</button>
      <button 
        type="button" 
        class="action-btn primary-btn" 
        [disabled]="isUpdatingSetting" 
        (click)="saveSettings()">
        {{ isUpdatingSetting ? 'Saving Settings...' : '💾 Save Settings' }}
      </button>
    </div>
  </div>
</div>

<!-- ==================== SAFE EVIDENCE LIGHTBOX MODAL ==================== -->
<div *ngIf="activeEvidenceLightboxUrl" class="modal-overlay nested-modal" (click)="closeEvidenceLightbox()">
  <div class="app-modal lightbox-modal" (click)="$event.stopPropagation()">
    <div class="modal-sticky-header">
      <h2>{{ activeEvidenceLightboxTitle || 'Evidence Preview' }}</h2>
      <button type="button" class="close-btn-static" (click)="closeEvidenceLightbox()">&times;</button>
    </div>
    <div class="modal-body-scrollable lightbox-body">
      <img [src]="activeEvidenceLightboxUrl" [alt]="activeEvidenceLightboxTitle || 'Full Evidence Image'" class="lightbox-img" />
    </div>
    <div class="modal-footer-actions">
      <button type="button" class="action-btn utility-btn" (click)="closeEvidenceLightbox()">Close Preview</button>
    </div>
  </div>
</div>


